import PropTypes from 'prop-types';
import { Lock, Plus, X } from 'lucide-react';
import { STAT_CARD_BASE } from '../../ui/StatCard';
import { EditorHeading, Field, LinesInput, TextArea, TextInput, inputCls } from './EditorFields';

/** Sentinel for "someone who is not a playable team" in the speaker picker. */
const OUTSIDE = '__outside__';

/**
 * One round: what the room reads, and the trainer's own debrief notes for it.
 *
 * The voices are each team's position in its own words, plus at least one person
 * outside the teams (a plant head, a director) — that outside voice is what makes it
 * read like a real organisation rather than teams in a vacuum.
 */
const RoundEditor = ({ round, functions, onChange }) => {
  const set = (field, value) => onChange({ ...round, [field]: value });
  const conflict = round.conflict || [];
  const setLine = (i, patch) => set('conflict', conflict.map((c, x) => (x === i ? { ...c, ...patch } : c)));

  const chooseSpeaker = (i, value) => {
    if (value === OUTSIDE) return setLine(i, { speakerKey: null, speakerLabel: '' });
    const fn = functions.find((f) => f.key === value);
    return setLine(i, { speakerKey: value, speakerLabel: fn?.name || value });
  };

  const debrief = round.debrief || {};
  const questions = debrief.facilitatorQuestions || [];
  // Questions are edited as lines; each keeps the debrief phase it was written for, and
  // new ones default to "what happened".
  const setQuestions = (lines) =>
    set('debrief', {
      ...debrief,
      facilitatorQuestions: lines.map((text, i) => ({ phase: questions[i]?.phase || 'what', text })),
    });

  return (
    <div className="space-y-4">
      <div className={`${STAT_CARD_BASE} space-y-3 p-5`}>
        <EditorHeading title="What the room reads" />
        <Field label="Title">
          <TextInput value={round.title} onChange={(v) => set('title', v)} />
        </Field>
        <Field label="The situation">
          <TextArea value={round.context} onChange={(v) => set('context', v)} rows={4} />
        </Field>
        <Field label="The decision" hint="Phrased so no option is obviously right.">
          <TextInput value={round.challenge} onChange={(v) => set('challenge', v)} />
        </Field>
        <Field label="What each choice costs" hint="One per line — what a choice buys, and what it costs.">
          <LinesInput value={round.decisionTensions} onChange={(v) => set('decisionTensions', v)} rows={3} />
        </Field>
      </div>

      <div className={`${STAT_CARD_BASE} p-5`}>
        <EditorHeading title="Voices in the room">
          Each team&apos;s position in its own words. Keep at least one voice from outside the
          teams.
        </EditorHeading>
        <div className="space-y-2.5">
          {conflict.map((line, i) => (
            <div key={i} className="rounded-xl border border-neutral-200 p-3 dark:border-neutral-700/60">
              <div className="flex items-start gap-2">
                <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-[1fr_1fr_0.7fr]">
                  <select
                    value={line.speakerKey || OUTSIDE}
                    onChange={(e) => chooseSpeaker(i, e.target.value)}
                    aria-label="Who is speaking"
                    className={inputCls}
                  >
                    {functions.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
                    <option value={OUTSIDE}>Someone outside the teams</option>
                  </select>
                  {line.speakerKey ? (
                    <div className="hidden sm:block" />
                  ) : (
                    <TextInput
                      value={line.speakerLabel}
                      onChange={(v) => setLine(i, { speakerLabel: v })}
                      placeholder="Their title — e.g. Plant Head"
                      aria-label="Speaker title"
                    />
                  )}
                  <TextInput value={line.verb} onChange={(v) => setLine(i, { verb: v })} placeholder="says" aria-label="Verb" />
                </div>
                <button
                  type="button"
                  onClick={() => set('conflict', conflict.filter((_, x) => x !== i))}
                  className="mt-1 shrink-0 rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-red-600 dark:hover:bg-neutral-800"
                  aria-label="Remove this voice"
                >
                  <X size={15} strokeWidth={2} />
                </button>
              </div>
              <div className="mt-2">
                <TextArea
                  value={line.statement}
                  onChange={(v) => setLine(i, { statement: v })}
                  rows={2}
                  placeholder="What they say"
                  aria-label="Statement"
                />
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => set('conflict', [
            ...conflict,
            { speakerKey: functions[0]?.key || null, speakerLabel: functions[0]?.name || '', verb: 'says', statement: '' },
          ])}
          className="mt-3 w-full rounded-xl border-2 border-dashed border-neutral-300 py-2 text-xs font-medium text-neutral-500 hover:border-neutral-400 hover:text-neutral-700 dark:border-neutral-700 dark:hover:border-neutral-600"
        >
          <Plus size={14} strokeWidth={2} className="mr-1 inline" />
          Add a voice
        </button>
      </div>

      <div className={`${STAT_CARD_BASE} space-y-3 p-5`}>
        <EditorHeading
          title={(
            <span className="flex items-center gap-1.5">
              <Lock size={13} strokeWidth={2} className="text-neutral-400" />
              Your debrief notes
            </span>
          )}
        >
          Shown on your console after this round. The room hears the teaching point in the
          final debrief, after the whole simulation; the questions stay yours.
        </EditorHeading>
        <Field label="Teaching point">
          <TextArea
            value={debrief.teachingPoint}
            onChange={(v) => set('debrief', { ...debrief, teachingPoint: v })}
            rows={2}
          />
        </Field>
        <Field label="Questions to ask" hint="One per line. Open questions — nothing answerable with yes or no.">
          <LinesInput value={questions.map((q) => q.text)} onChange={setQuestions} rows={4} />
        </Field>
      </div>
    </div>
  );
};

RoundEditor.propTypes = {
  round: PropTypes.object.isRequired,
  functions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default RoundEditor;
